import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, KeyRound, UserX, UserCheck, ShieldCheck, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useUser } from '../context/UserContext';

const normalize = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = { admin: 'Administrador', editor: 'Editor', viewer: 'Solo lectura' };

const errorText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg?.replace('Value error, ', '')).join(' ');
  return 'Ha ocurrido un error';
};

const RoleSelect = ({ value, onChange, disabled, testId }) => (
  <Select value={value} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger className="w-[150px] h-8" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      {Object.entries(ROLE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

const NewUserDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState({ name: '', email: '', role: 'editor' });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/users`, form);
      toast.success(`Usuario creado. Contraseña inicial: ${data.temp_password}`, { duration: 10000 });
      setForm({ name: '', email: '', role: 'editor' });
      onOpenChange(false);
      onCreated();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-user-dialog">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Nuevo usuario</DialogTitle>
            <DialogDescription>Entrará con la contraseña inicial y tendrá que cambiarla en su primer acceso.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="nu-name">Nombre completo</Label>
            <Input id="nu-name" data-testid="new-user-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="nu-email">Email</Label>
            <Input id="nu-email" type="email" data-testid="new-user-email-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Permisos</Label>
            <RoleSelect value={form.role} onChange={(role) => setForm({ ...form, role })} testId="new-user-role-select" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid="new-user-submit-button">{saving ? 'Guardando…' : 'Dar de alta'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const UserRow = ({ u, isSelf, onRole, onAsk }) => (
  <div data-testid={`user-row-${u.email}`} className={`flex flex-col md:flex-row md:items-center gap-3 py-3 border-b last:border-0 ${u.active ? '' : 'opacity-60'}`}>
    <div className="flex-1 min-w-0">
      <div className="font-medium truncate flex items-center gap-2">
        {u.name}
        {isSelf && <Badge variant="outline" className="text-[10px]">Tú</Badge>}
        {!u.active && <Badge variant="secondary" data-testid={`user-inactive-badge-${u.email}`}>Desactivado</Badge>}
        {u.active && !u.has_logged_in && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]">Pendiente primer acceso</Badge>}
      </div>
      <div className="text-xs text-muted-foreground truncate">{u.email}</div>
    </div>
    <div className="flex items-center gap-2 flex-wrap">
      <RoleSelect value={u.role} onChange={(r) => onRole(u, r)} disabled={isSelf || !u.active} testId={`user-role-select-${u.email}`} />
      <Button size="sm" variant="outline" onClick={() => onAsk(u, 'reset')} disabled={isSelf || !u.active} data-testid={`user-reset-password-${u.email}`}>
        <KeyRound className="w-4 h-4 mr-1" /> Restablecer
      </Button>
      <Button size="sm" variant="outline" onClick={() => onAsk(u, 'toggle')} disabled={isSelf} data-testid={`user-toggle-active-${u.email}`}
        className={u.active ? 'text-destructive' : 'text-green-700'}>
        {u.active ? <><UserX className="w-4 h-4 mr-1" /> Desactivar</> : <><UserCheck className="w-4 h-4 mr-1" /> Reactivar</>}
      </Button>
    </div>
  </div>
);

export default function UsersAdmin() {
  const { user, isAdmin } = useUser();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`);
      setUsers(data);
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const patch = async (u, body, msg) => {
    try {
      await axios.patch(`${API}/admin/users/${encodeURIComponent(u.email)}`, body);
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  const runConfirm = async () => {
    const { u, action } = confirm;
    setConfirm(null);
    if (action === 'toggle') return patch(u, { active: !u.active }, u.active ? `${u.name} desactivado` : `${u.name} reactivado`);
    try {
      const { data } = await axios.post(`${API}/admin/users/${encodeURIComponent(u.email)}/reset-password`);
      toast.success(`Contraseña restablecida a ${data.temp_password}`, { duration: 10000 });
      load();
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  if (!isAdmin) {
    return <div className="p-8 text-muted-foreground" data-testid="users-admin-forbidden">Solo los administradores pueden acceder a esta sección.</div>;
  }

  const activeCount = users.filter((u) => u.active).length;
  const q = normalize(query.trim());
  const filtered = q ? users.filter((u) => normalize(`${u.name} ${u.email}`).includes(q)) : users;

  return (
    <div className="p-4 md:p-8 max-w-5xl space-y-6" data-testid="users-admin-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2"><ShieldCheck className="w-7 h-7" /> Usuarios</h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="users-admin-count">{activeCount} activos · {users.length - activeCount} desactivados</p>
        </div>
        <Button onClick={() => setNewOpen(true)} data-testid="new-user-button"><UserPlus className="w-4 h-4 mr-2" /> Nuevo usuario</Button>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nombre o email…"
          className="pl-9"
          data-testid="users-search-input"
        />
      </div>

      <Card>
        <CardContent className="py-2">
          {loading ? <div className="py-8 text-center text-muted-foreground">Cargando…</div> : filtered.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground" data-testid="users-search-empty">No hay usuarios que coincidan con "{query}"</div>
          ) : filtered.map((u) => (
            <UserRow key={u.email} u={u} isSelf={u.email === user?.email?.toLowerCase()}
              onRole={(x, role) => patch(x, { role }, `${x.name} ahora es ${ROLE_LABELS[role]}`)}
              onAsk={(x, action) => setConfirm({ u: x, action })} />
          ))}
        </CardContent>
      </Card>

      <NewUserDialog open={newOpen} onOpenChange={setNewOpen} onCreated={load} />

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="user-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.action === 'reset' ? 'Restablecer contraseña' : confirm?.u.active ? 'Desactivar usuario' : 'Reactivar usuario'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.action === 'reset'
                ? `${confirm?.u.name} volverá a la contraseña inicial y tendrá que cambiarla al entrar.`
                : confirm?.u.active
                  ? `${confirm?.u.name} dejará de poder entrar. Sus datos se conservan.`
                  : `${confirm?.u.name} podrá volver a entrar con su contraseña actual.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-confirm-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={runConfirm} data-testid="user-confirm-accept">Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
